'use client';
import React, { useState } from 'react';
import { Menu, Bell, Search, ChevronDown, Shield, RefreshCw, LogOut } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { useRouter } from 'next/navigation';

interface TopbarProps {
  onToggleSidebar: () => void;
  onMobileMenuOpen: () => void;
  sidebarCollapsed: boolean;
}

export default function Topbar({ onToggleSidebar, onMobileMenuOpen }: TopbarProps) {
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const router = useRouter();

  const handleSignOut = async () => {
    await signOut();
    router.push('/sign-up-login-screen');
  };

  const notifications = [
    { id: 'notif-1', text: 'Credit card bill due in 3 days', type: 'warning', time: '2h ago' },
    { id: 'notif-2', text: 'AI detected duplicate Netflix subscription', type: 'info', time: '4h ago' },
    { id: 'notif-3', text: 'Park-N-Lot budget 87% utilized', type: 'danger', time: '1d ago' },
  ];

  const initials = user?.email ? user.email.slice(0, 2).toUpperCase() : 'AJ';

  return (
    <header className="h-14 bg-card border-b border-border flex items-center px-4 gap-3 flex-shrink-0 z-30">
      <button onClick={onToggleSidebar} className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 active:scale-95" aria-label="Toggle sidebar">
        <Menu size={18} />
      </button>
      <button onClick={onMobileMenuOpen} className="lg:hidden flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150" aria-label="Open menu">
        <Menu size={18} />
      </button>

      <div className="flex-1 max-w-md hidden sm:flex items-center gap-2 bg-muted rounded-lg px-3 py-1.5 border border-border">
        <Search size={14} className="text-muted-foreground flex-shrink-0" />
        <input type="text" placeholder="Search transactions, merchants..." className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none flex-1 min-w-0" />
        <kbd className="hidden lg:block text-[10px] text-muted-foreground bg-background px-1.5 py-0.5 rounded border border-border font-mono">⌘K</kbd>
      </div>

      <div className="flex-1 lg:flex-none" />

      {/* Notifications */}
      <div className="relative">
        <button onClick={() => { setNotifOpen(!notifOpen); setUserMenuOpen(false); }} className="relative flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 active:scale-95" aria-label="Notifications">
          <Bell size={18} />
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-danger animate-pulse-gold" />
        </button>
        {notifOpen && (
          <div className="absolute right-0 top-10 w-80 bg-card border border-border rounded-xl shadow-2xl z-50 animate-slide-up overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <span className="text-sm font-600 text-foreground">Alerts</span>
              <span className="text-xs text-accent">{notifications.length} new</span>
            </div>
            {notifications.map((n) => (
              <div key={n.id} className="px-4 py-3 border-b border-border/50 hover:bg-muted/50 transition-colors cursor-pointer">
                <div className="flex items-start gap-3">
                  <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${n.type === 'warning' ? 'bg-warning' : n.type === 'danger' ? 'bg-danger' : 'bg-info'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground leading-snug">{n.text}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{n.time}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground">
        <RefreshCw size={12} className="text-success" />
        <span>Live</span>
      </div>

      {/* User menu */}
      <div className="relative">
        <button
          onClick={() => { setUserMenuOpen(!userMenuOpen); setNotifOpen(false); }}
          className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted transition-all duration-150 active:scale-95"
        >
          <div className="w-7 h-7 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center">
            <span className="text-xs font-700 text-accent">{initials}</span>
          </div>
          <div className="hidden md:flex flex-col items-start">
            <span className="text-xs font-600 text-foreground leading-tight">A. Jefferson</span>
            <span className="text-[10px] text-muted-foreground leading-tight">Private Account</span>
          </div>
          <ChevronDown size={14} className="text-muted-foreground hidden md:block" />
        </button>

        {userMenuOpen && (
          <div className="absolute right-0 top-10 w-56 bg-card border border-border rounded-xl shadow-2xl z-50 animate-slide-up overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <p className="text-xs font-600 text-foreground truncate">{user?.email}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Authorized account</p>
            </div>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-danger hover:bg-danger/10 transition-colors"
            >
              <LogOut size={14} />
              Sign Out
            </button>
          </div>
        )}
      </div>

      <div className="hidden lg:flex items-center gap-1 px-2 py-1 rounded-md bg-success/10 border border-success/20">
        <Shield size={12} className="text-success" />
        <span className="text-[10px] font-600 text-success">Secure</span>
      </div>
    </header>
  );
}